import { useEffect } from "react"
import { STATUS, type Status, type Step, useJoyride } from "react-joyride"
import { useLocation } from "react-router-dom"

import { IS_AUTH } from "../../modules/auth"
import type { TourId } from "../../modules/tour-start-store"
import {
  markTourDismissed,
  useTourStartRequest,
} from "../../modules/tour-start-store"

// The generic shell tour — its dismissal key survived the per-app split, so
// users who already dismissed it keep that dismissal.
const TOUR_ID: TourId = "guide"

// Targets present on every app: the layout shell and the settings gear. The
// tour runs for non-auth users on every route (mounted once in SharedLayout).
// Normal sortaballs routes swap this generic tour for the game's own gameplay
// tour, so the two never overlap.
const STEPS: Step[] = [
  {
    content: "Welcome! This is the app. Use the gear to open the settings panel.",
    skipBeacon: true,
    target: ".layout__outer",
  },
  {
    content: "Open the settings to pick another app or change the theme.",
    skipBeacon: true,
    target: ".settings-open, .settings-closed",
  },
]

// Select-correct-word, reveal-it and have-you-seen-it run their own gameplay
// tours, so the generic shell guide is suppressed on those routes.
export const GAMEPLAY_TOUR_ROUTE =
  /\/select-correct-word$|\/reveal-it$|\/have-you-seen-it$/

const TOUR_END_STATUSES = [STATUS.FINISHED, STATUS.SKIPPED] as Status[]

export function GuideTour() {
  const { pathname } = useLocation()
  const isGameplayTourRoute = GAMEPLAY_TOUR_ROUTE.test(pathname)
  const tourStartRequest = useTourStartRequest()
  const { controls, Tour } = useJoyride({
    continuous: true,
    onEvent: (data)=> {
      // Persist whenever the user dismisses or completes the tour so it is not
      // shown again. Close/skip/finish all count as "done".
      if (
        data.action === "close" ||
        data.action === "skip" ||
        TOUR_END_STATUSES.includes(data.status)
      ) {
        markTourDismissed(TOUR_ID)
      }
    },
    steps: STEPS,
  })

  // Manual Start Tour from the sidebar only — the shell guide never
  // auto-starts: the per-game tours own their routes' first visits.
  // Rule: once started, we set its flag so it is never auto-started again.
  useEffect(()=> {
    if (IS_AUTH) {
      return
    }
    if (tourStartRequest === 0) {
      return
    }
    markTourDismissed(TOUR_ID)
    controls.start()
  }, [controls, tourStartRequest])

  if (IS_AUTH || isGameplayTourRoute) {
    return null
  }

  return Tour
}